/**
 * The optional "import from an estimate" picker on the New Resource Plan form.
 *
 * A plain authenticated pass-through to the Solution API's own `/projects` list — no
 * Account/company matching (the two systems' naming didn't line up in practice) and no
 * capability gate beyond being logged in. Every project is returned, grouped by
 * `prospectCompany` so a human can find the right one in a list of a hundred-plus. Mirrors
 * `useSolutionPickerOptions` otherwise: one query, one key from the factory, a `select`
 * that shapes the response into `SelectOption[]` for `SearchableSelect`.
 */

import { useQuery } from "@tanstack/react-query";
import type { SolutionEstimateProjectSummary } from "@/api/solution-estimate-projects";
import { jsonFetch } from "@/lib/json-fetch";
import { sortOptions } from "@/lib/option-search";
import { solutionEstimateProjectKeys } from "@/lib/query";
import type { EstimateSpan } from "@/lib/solution-estimate/anchor";

interface SolutionEstimateProjectsResponse {
  projects: SolutionEstimateProjectSummary[];
}

export function useSolutionEstimateProjects(enabled: boolean) {
  return useQuery({
    queryKey: solutionEstimateProjectKeys.all,
    queryFn: () => jsonFetch<SolutionEstimateProjectsResponse>("/api/solution-estimate-projects"),
    enabled,
    select: (data) =>
      sortOptions(
        data.projects.map((p) => ({
          value: p.id,
          label: p.name,
          group: p.prospectCompany.trim() === "" ? "No company listed" : p.prospectCompany,
        })),
      ),
  });
}

/**
 * How many months the picked estimate runs. The plan's End Month is derived from it — the
 * estimate's first month lands on the chosen Start Month — so the form can show the end
 * rather than ask for one. Disabled until a project is picked.
 */
export function useSolutionEstimateSpan(projectId: string) {
  return useQuery({
    queryKey: solutionEstimateProjectKeys.span(projectId),
    queryFn: () =>
      jsonFetch<{ span: EstimateSpan | null }>(
        `/api/solution-estimate-projects/${encodeURIComponent(projectId)}/span`,
      ),
    enabled: projectId !== "",
    select: (data) => data.span,
  });
}
